<template>
  <div class="request-page">
    <div class="page-header">
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-hand-holding-dollar"></i>
        </div>
        <div>
          <h1 class="page-title">Request a concession</h1>
          <p class="page-subtitle">
            <span v-if="loan">{{ borrowerName }} &middot; {{ loan.loan_number || 'no loan number' }}</span>
            <span v-else-if="manual">Entered by hand — nothing is filled in for you</span>
            <span v-else>Loading the loan...</span>
          </p>
        </div>
      </div>
    </div>

    <div v-if="loading" class="state-card">Loading...</div>

    <div v-else-if="loadError" class="blocked-card">
      <div class="blocked-icon"><i class="fa-duotone fa-circle-exclamation"></i></div>
      <div>
        <div class="blocked-title">Couldn't open that loan</div>
        <div class="blocked-text">{{ loadError }}</div>
        <div class="blocked-text">
          Close this tab, or <a href="/concessions/request">enter a loan by hand</a>.
        </div>
      </div>
    </div>

    <template v-else>
      <div v-if="context.roster_note" class="roster-note-card">
        <div class="roster-note-icon"><i class="fa-duotone fa-circle-info"></i></div>
        <div class="roster-note-text">{{ context.roster_note }}</div>
      </div>

      <!-- Two columns: what the loan already is on the left, what we are asking for
           on the right. The left half is reference material the requester reads while
           filling in the right half, so they have to be side by side rather than one
           scrolled past the other. -->
      <div class="request-split">
        <!-- ── the loan ── -->
        <section class="panel">
          <h2 class="panel-title">
            <i class="fa-duotone fa-file-invoice-dollar"></i> The loan
          </h2>

          <!-- Editable ONLY on the manual path. A loan that came from the pipeline is
               already settled, and letting the id be edited here would let it drift
               away from the figures the decision is being made against. -->
          <div v-if="manual" class="form-grid">
            <!-- No GUID field: the server looks the Encompass loan id up from this number,
                 which is what Polly hangs the price exception on. -->
            <label class="form-field">
              <span class="form-label">Loan number <span class="req">*</span></span>
              <input v-model.trim="form.loan_number" class="form-input" type="text" />
              <span class="form-hint">The loan is found from this number when you preview or submit.</span>
            </label>
            <label class="form-field">
              <span class="form-label">Borrower first</span>
              <input v-model.trim="form.borrower_first" class="form-input" type="text" />
            </label>
            <label class="form-field">
              <span class="form-label">Borrower last</span>
              <input v-model.trim="form.borrower_last" class="form-input" type="text" />
            </label>
            <label class="form-field">
              <span class="form-label">Loan amount</span>
              <input v-model.number="form.loan_amount" class="form-input" type="number"
                     step="0.01" min="0" />
              <span class="form-hint">Used to convert between bps and dollars.</span>
            </label>
          </div>

          <template v-else>
            <dl class="fact-list">
              <div class="fact"><dt>Borrower</dt><dd>{{ borrowerName }}</dd></div>
              <div class="fact"><dt>Loan number</dt><dd>{{ loan.loan_number || '—' }}</dd></div>
              <div class="fact">
                <dt>Loan officer</dt>
                <dd>{{ loan.loan_officer_name || loan.loan_officer_email || '—' }}</dd>
              </div>
              <div class="fact"><dt>Loan amount</dt><dd>{{ formatMoney(loan.loan_amount) }}</dd></div>
              <div class="fact"><dt>Rate</dt><dd>{{ formatRate(loan.interest_rate) }}</dd></div>
              <div class="fact">
                <dt>Product</dt>
                <dd>
                  {{ loan.product_type ? productLabel(loan.product_type) : (loan.product_type_raw || '—') }}
                  <span v-if="!loan.product_type && loan.product_type_raw" class="diag-flag is-warn">
                    unmapped
                  </span>
                </dd>
              </div>
              <div class="fact"><dt>Program</dt><dd>{{ loan.loan_program || '—' }}</dd></div>
              <div class="fact">
                <dt>Lock expires</dt>
                <dd>
                  {{ formatLoanDate(loan.lock_expires) }}
                  <span v-if="lockExpiryLabel" class="sub-note" :class="{ 'is-warn': lockUrgent }">
                    {{ lockExpiryLabel }}
                  </span>
                </dd>
              </div>
              <div class="fact"><dt>Milestone</dt><dd>{{ loan.current_milestone || '—' }}</dd></div>
              <div class="fact">
                <dt>Est. closing</dt>
                <dd>{{ formatLoanDate(loan.estimated_closing_date) }}</dd>
              </div>
            </dl>

            <!-- Margins are for APPROVERS. Filing a concession is open to everyone, so
                 reaching this page says nothing about whether you may read profit: both
                 figures now require a row in the approver roster for THIS loan's division
                 (branch column for the branch figure, corp column for the corporate one),
                 with the lock desk and concessions admins seeing both everywhere.

                 Withheld by the SERVER — an ineligible viewer's response carries no margin
                 at all, so there is nothing to find in the network tab. Whoever may see
                 neither gets no heading either, rather than a strip of em dashes that reads
                 as "this loan has no margins". -->
            <template v-if="showMargins">
              <h3 class="panel-subtitle">Margins</h3>
              <div class="on-file-strip">
                <div v-if="loan.can_see_branch_margin" class="on-file-cell">
                  <div class="on-file-value">{{ loan.branch_margin || '—' }}</div>
                  <div class="on-file-label">Branch</div>
                </div>
                <div v-if="loan.can_see_corp_margin" class="on-file-cell">
                  <div class="on-file-value">{{ loan.corp_margin || '—' }}</div>
                  <div class="on-file-label">Corporate</div>
                </div>
              </div>
            </template>

            <h3 class="panel-subtitle">Already on the file</h3>
            <div class="on-file-strip">
              <div class="on-file-cell is-total">
                <div class="on-file-value">
                  {{ bpsLabel(loan.concession_on_file_bps, loan.concession_on_file_amount) }}
                </div>
                <div class="on-file-label">Total conceded</div>
              </div>
              <div class="on-file-cell">
                <div class="on-file-value">{{ bpsOf(loan.corp_concession_amount) }}</div>
                <div class="on-file-label">Corporate</div>
              </div>
              <div class="on-file-cell">
                <div class="on-file-value">{{ bpsOf(loan.branch_concession_amount) }}</div>
                <div class="on-file-label">Branch</div>
              </div>
              <div class="on-file-cell">
                <div class="on-file-value">{{ bpsOf(loan.lender_credit_amount) }}</div>
                <div class="on-file-label">Lender credit</div>
              </div>
            </div>

            <div v-if="swiftNote" class="on-file-note">{{ swiftNote }}</div>

            <div class="raised-under">
              Raised under
              <strong>{{ loan.loan_officer_name || loan.loan_officer_email || 'the loan officer on file' }}</strong>
              — their ceilings and approvers are what apply, not yours.
            </div>
          </template>
        </section>

        <!-- ── the request ── -->
        <section class="panel">
          <h2 class="panel-title">
            <i class="fa-duotone fa-file-circle-plus"></i> The request
          </h2>

          <div class="requester-line">
            Filing as <strong>{{ context.email }}</strong>
            <span v-if="context.division"> &middot; {{ context.division }}</span>
            <span v-if="context.branch_name"> &middot; {{ context.branch_name }}</span>
          </div>

          <div class="form-grid">
            <label class="form-field">
              <span class="form-label">Product <span class="req">*</span></span>
              <select v-model="form.product_type" class="form-input">
                <option value="">Select a product...</option>
                <option v-for="p in context.products" :key="p" :value="p">{{ productLabel(p) }}</option>
              </select>
              <span v-if="loan && !loan.product_type" class="form-hint is-warn">
                Encompass has "{{ loan.product_type_raw || 'nothing' }}" on this loan, which isn't
                one we price against — pick the right one.
              </span>
            </label>

            <label class="form-field">
              <span class="form-label">Concession type <span class="req">*</span></span>
              <select v-model="form.tier" class="form-input">
                <option value="branch">Branch</option>
                <option value="corp">Corporate</option>
                <option value="split">Split between both</option>
              </select>
              <span v-if="isSplit" class="form-hint">
                Filed as two requests, one per side — each is judged on its own ceiling, so
                they can be decided separately.
              </span>
            </label>

            <label class="form-field">
              <span class="form-label">Reason <span class="req">*</span></span>
              <select v-model="form.reason_code" class="form-input">
                <option value="">Select a reason...</option>
                <option v-for="r in reasons" :key="r.id || r.code" :value="r.code">
                  {{ r.label }}
                </option>
              </select>
              <span v-if="!reasons.length" class="form-hint is-warn">
                No reasons available — the Polly reason sync hasn't run yet.
              </span>
              <span v-else-if="selectedReasonRequiresNote" class="form-hint">
                This reason requires a note.
              </span>
            </label>
          </div>

          <!-- A split asks for each side separately, because each side is a separate
               request judged against a separate ceiling. Both in bps, since that is the
               unit the ceilings are set in and the unit this page reads in. -->
          <template v-if="isSplit">
            <div class="amount-row">
              <label class="form-field">
                <span class="form-label">Branch pays (bps) <span class="req">*</span></span>
                <input v-model.number="form.split_branch_bps" class="form-input" type="number"
                       step="0.01" min="0" />
                <span v-if="splitBranchDollars" class="form-hint">≈ {{ splitBranchDollars }}</span>
              </label>
              <div class="amount-or">+</div>
              <label class="form-field">
                <span class="form-label">Corporate pays (bps) <span class="req">*</span></span>
                <input v-model.number="form.split_corp_bps" class="form-input" type="number"
                       step="0.01" min="0" />
                <span v-if="splitCorpDollars" class="form-hint">≈ {{ splitCorpDollars }}</span>
              </label>
            </div>
            <div class="split-total">
              <span v-if="splitTotalBps > 0">
                Total concession <strong>{{ formatNumber(splitTotalBps) }} bps</strong>
                <span v-if="splitTotalDollars"> &middot; {{ splitTotalDollars }}</span>
              </span>
              <span v-else class="is-muted">Enter what each side pays.</span>
            </div>
          </template>

          <!-- No min: a NEGATIVE amount is how a concession comes back off the file. Polly
               has no call that edits or cancels a price exception, so a reduction is filed as
               a new request in the other direction. Givebacks auto-approve — the server
               decides that, this only lets the number be typed. -->
          <div v-else class="amount-row">
            <label class="form-field">
              <span class="form-label">Amount (bps)</span>
              <input v-model.number="form.requested_bps" class="form-input" type="number"
                     step="0.01" @input="form.requested_amount = null" />
            </label>
            <div class="amount-or">or</div>
            <label class="form-field">
              <span class="form-label">Amount ($)</span>
              <input v-model.number="form.requested_amount" class="form-input" type="number"
                     step="0.01" @input="form.requested_bps = null" />
            </label>
            <div v-if="derivedAmountLabel" class="amount-derived">{{ derivedAmountLabel }}</div>
          </div>
          <div v-if="isGiveback" class="giveback-hint">
            <i class="fa-duotone fa-rotate-left"></i>
            Negative amount — this reduces a concession already on the file rather than
            granting one, and auto-approves.
          </div>

          <!-- What the file would carry if this were approved. The point of starting
               from the loan is that this stops being a guess. -->
          <div v-if="runningTotalLabel" class="running-total">{{ runningTotalLabel }}</div>

          <label class="form-field full">
            <span class="form-label">
              Notes <span v-if="selectedReasonRequiresNote" class="req">*</span>
            </span>
            <textarea v-model.trim="form.requester_notes" class="form-input form-textarea" rows="3"
                      placeholder="Anything the approver should know..."></textarea>
          </label>

          <div v-if="submitError" class="inline-error">{{ submitError }}</div>

          <div v-if="submitResult" class="outcome-card"
               :class="isPreview ? 'is-preview' : (submitResult.auto_approved && !anyQueued ? 'is-approved' : 'is-pending')">
            <div class="outcome-head">
              <i :class="isPreview
                         ? 'fa-duotone fa-eye'
                         : (queuedUnfiled
                            ? 'fa-duotone fa-clock-rotate-left'
                            : (submitResult.auto_approved
                               ? 'fa-duotone fa-circle-check'
                               : 'fa-duotone fa-user-clock'))"></i>
              <strong v-if="isPreview">
                Preview — nothing saved.
                {{ submitResult.auto_approved ? 'This would auto-approve' : 'This would need approval' }}
              </strong>
              <strong v-else-if="queuedUnfiled">Queued for the lock desk</strong>
              <strong v-else>{{ submitResult.auto_approved ? 'Auto-approved' : 'Sent for approval' }}</strong>
            </div>

            <div v-if="submitResult.polly && submitResult.polly.prior_final_price !== null &&
                       submitResult.polly.prior_final_price !== undefined"
                 class="outcome-pricing">
              <span>Price before: <strong>{{ formatPrice(submitResult.polly.prior_final_price) }}</strong></span>
              <span>after: <strong>{{ formatPrice(submitResult.polly.exception_final_price) }}</strong></span>
              <span v-if="priceDelta !== null">
                change: <strong>{{ priceDelta > 0 ? '+' : '' }}{{ formatPrice(priceDelta) }}</strong>
              </span>
            </div>

            <div v-if="submitResult.explanation" class="outcome-text">{{ submitResult.explanation }}</div>

            <!-- The halves can land differently, so each one states its own outcome
                 rather than hiding behind the combined line above. -->
            <div v-if="submitResult.parts && submitResult.parts.length" class="split-parts">
              <div v-for="(part, i) in submitResult.parts" :key="i" class="split-part">
                <span class="split-part-tier">{{ i === 0 ? 'Branch' : 'Corporate' }}</span>
                <span class="split-part-body">
                  <strong>
                    {{ isQueued(part) && !part.request
                       ? 'Queued'
                       : (part.auto_approved ? 'Approved' : 'Needs approval') }}
                  </strong>
                  <span v-if="part.request && part.request.requested_bps">
                    &middot; {{ formatNumber(part.request.requested_bps) }} bps
                  </span>
                  <span v-if="part.routed_to && part.routed_to.length">
                    &middot; waiting on {{ part.routed_to.join(', ') }}
                  </span>
                  <span v-if="part.polly && part.polly.pe_request_id" class="is-ok">
                    &middot; Polly {{ part.polly.pe_request_id }}
                  </span>
                  <span v-if="part.polly_error" class="is-warn">
                    &middot; Polly hasn't taken it: {{ part.polly_error }}
                  </span>
                  <span v-if="isQueued(part)" class="is-warn">
                    &middot; queued for the lock desk
                  </span>
                </span>
              </div>
            </div>
            <div v-if="submitResult.routed_to && submitResult.routed_to.length" class="outcome-text">
              Waiting on {{ submitResult.routed_to.join(', ') }}.
            </div>
            <div v-else-if="!submitResult.auto_approved && !anyQueued" class="outcome-text">
              No approvers are configured for your division yet, so the lock desk will review it.
            </div>

            <div v-if="submitResult.polly_error" class="outcome-text is-warn">
              <i class="fa-duotone fa-triangle-exclamation"></i>
              Recorded here, but Polly hasn't accepted it yet: {{ submitResult.polly_error }}
            </div>
            <div v-else-if="submitResult.polly && submitResult.polly.pe_request_id"
                 class="outcome-text is-ok">
              Polly price exception created ({{ submitResult.polly.pe_request_id }}).
            </div>
            <div v-if="submitResult.lock_desk_queue_id" class="outcome-text is-ok">
              Recorded on the Lock Desk Queue as a completed item.
            </div>

            <!-- The immediate attempt couldn't finish. The request is saved and queued, so say
                 outright that it must not be filed again — a second submit would be a second
                 concession. -->
            <div v-if="!isPreview && anyQueued" class="outcome-text is-warn">
              <i class="fa-duotone fa-clock-rotate-left"></i>
              {{ queuedLead }} It's queued and will be processed by the lock desk shortly —
              you don't need to submit it again.
            </div>

            <!-- Filed, and this tab has nothing left to do. Say so, rather than
                 leaving a spent form on screen that looks re-submittable. -->
            <div v-if="filed" class="outcome-done">
              This request is {{ anyQueued ? 'queued' : 'filed' }}. You can close this tab.
            </div>
          </div>

          <div class="form-actions">
            <button class="cancel-btn" :disabled="submitting" @click="resetForm">Clear</button>
            <button class="preview-btn" :disabled="submitting || !canSubmit || filed" @click="preview">
              {{ previewing ? 'Checking...' : 'Preview' }}
            </button>
            <button class="save-btn" :disabled="submitting || !canSubmit || filed" @click="submit">
              {{ submitting && !previewing ? 'Submitting...' : 'Submit concession' }}
            </button>
          </div>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
  import api from '@/api/api.service'
  import auth from '@/authConfig.js'
  import { productLabel } from '@/helpers/concessionProducts'

  export default {
    name: 'ConcessionRequest',
    props: {
      // From the route. Absent = the manual path.
      loanId: { type: String, default: null }
    },
    data() {
      return {
        loading: true,
        loadError: '',
        loan: null,
        context: {
          email: '',
          roster_note: '',
          division: '',
          branch_name: '',
          reasons: [],
          branch_reasons: [],
          products: []
        },
        form: this.emptyForm(),
        submitting: false,
        previewing: false,
        isPreview: false,
        submitError: '',
        submitResult: null,
        // A filed request must not be filable again from the same tab.
        filed: false
      }
    },
    computed: {
      manual() {
        return !this.loanId
      },
      // Both flags off means the viewer is in neither approver column for this loan's
      // division, so the whole block goes rather than each cell separately.
      showMargins() {
        return !!(this.loan && (this.loan.can_see_branch_margin || this.loan.can_see_corp_margin))
      },
      borrowerName() {
        const src = this.loan || this.form
        const name = [src.borrower_first, src.borrower_last].filter(Boolean).join(' ')
        return name || '—'
      },
      isSplit() {
        return this.form.tier === 'split'
      },
      // One list for branch, corporate and split alike. Polly types its reasons per tier,
      // but its create call doesn't tie the reason to the type, and the org has no
      // Corporate reasons — a per-tier list left corporate and split with nothing to pick.
      // branch_reasons is the fallback for an API that predates `reasons`.
      reasons() {
        const all = this.context.reasons || []
        return all.length ? all : (this.context.branch_reasons || [])
      },
      splitTotalBps() {
        return (Number(this.form.split_branch_bps) || 0) + (Number(this.form.split_corp_bps) || 0)
      },
      splitBranchDollars() {
        return this.bpsToMoney(this.form.split_branch_bps)
      },
      splitCorpDollars() {
        return this.bpsToMoney(this.form.split_corp_bps)
      },
      splitTotalDollars() {
        return this.bpsToMoney(this.splitTotalBps)
      },
      selectedReasonRequiresNote() {
        const r = this.reasons.find(x => x.code === this.form.reason_code)
        return !!(r && r.requires_note)
      },
      canSubmit() {
        // A pipeline loan arrives with its id; a hand-entered one is identified by number
        // and the server resolves the id from it.
        if (this.manual ? !this.form.loan_number : !this.form.loan_id) return false
        if (!this.form.product_type || !this.form.tier) return false
        if (!this.form.reason_code) return false
        if (this.selectedReasonRequiresNote && !this.form.requester_notes) return false

        // Both sides are required on a split. One side at zero is not a split — it is a
        // single-tier concession, and the server says so rather than filing an empty half.
        if (this.isSplit) {
          return (Number(this.form.split_branch_bps) || 0) > 0 &&
                 (Number(this.form.split_corp_bps) || 0) > 0
        }

        const bps = Number(this.form.requested_bps) || 0
        const amt = Number(this.form.requested_amount) || 0
        return bps !== 0 || amt !== 0
      },
      // A negative on either side. Drives the hint under the amount row; the server makes
      // the actual decision, so this is presentation only.
      isGiveback() {
        return (Number(this.form.requested_bps) || 0) < 0 ||
               (Number(this.form.requested_amount) || 0) < 0
      },
      derivedAmountLabel() {
        const loanAmount = Number(this.form.loan_amount) || 0
        if (loanAmount <= 0) return ''
        const bps = Number(this.form.requested_bps) || 0
        const amt = Number(this.form.requested_amount) || 0
        if (bps !== 0) return `≈ ${this.formatMoney((bps / 10000) * loanAmount)}`
        if (amt !== 0) return `≈ ${this.formatNumber((amt / loanAmount) * 10000)} bps`
        return ''
      },
      requestedDollars() {
        const loanAmount = Number(this.form.loan_amount) || 0

        // A split's ask is the two halves together — that is what lands on the file.
        if (this.isSplit) {
          return this.splitTotalBps > 0 && loanAmount > 0
            ? (this.splitTotalBps / 10000) * loanAmount
            : 0
        }

        const amount = Number(this.form.requested_amount) || 0
        if (amount !== 0) return amount
        const bps = Number(this.form.requested_bps) || 0
        return bps !== 0 && loanAmount > 0 ? (bps / 10000) * loanAmount : 0
      },
      runningTotalLabel() {
        if (!this.loan) return ''
        const asked = this.requestedDollars
        if (asked === 0) return ''
        // Signed, so a giveback shows the file coming DOWN — the whole point of filing one.
        const total = (Number(this.loan.concession_on_file_amount) || 0) + asked
        const base = Number(this.loan.total_loan_amount) || Number(this.loan.loan_amount) || 0
        if (base <= 0) return ''
        return `Approved, this file would carry ${this.formatNumber((total / base) * 10000)} bps in concessions.`
      },
      swiftNote() {
        if (!this.loan) return ''
        const parts = []
        if (this.loan.swift_pending_count) parts.push(`${this.loan.swift_pending_count} still pending`)
        if (this.loan.swift_approved_count) parts.push(`${this.loan.swift_approved_count} already approved`)
        return parts.length ? `This loan has ${parts.join(' and ')} through this page.` : ''
      },
      lockDaysLeft() {
        if (!this.loan || !this.loan.lock_expires) return null
        const expires = new Date(this.loan.lock_expires)
        if (Number.isNaN(expires.getTime())) return null
        const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
        return Math.round((startOfDay(expires) - startOfDay(new Date())) / 86400000)
      },
      lockExpiryLabel() {
        const days = this.lockDaysLeft
        if (days === null) return ''
        if (days < 0) return `expired ${Math.abs(days)}d ago`
        if (days === 0) return 'expires today'
        return `${days}d left`
      },
      lockUrgent() {
        return this.lockDaysLeft !== null && this.lockDaysLeft <= 7
      },
      priceDelta() {
        const before = this.submitResult?.polly?.prior_final_price
        const after = this.submitResult?.polly?.exception_final_price
        if (before === null || before === undefined) return null
        if (after === null || after === undefined) return null
        return Number(after) - Number(before)
      },
      // The submit's immediate attempt couldn't finish, for the request or either half of a
      // split, so it is queued for the lock desk.
      anyQueued() {
        const r = this.submitResult
        return this.isQueued(r) || (r?.parts || []).some(part => this.isQueued(part))
      },
      // Queued before it was even filed — there is no decision to show yet.
      queuedUnfiled() {
        return this.isQueued(this.submitResult) && !this.submitResult.request
      },
      queuedLead() {
        const r = this.submitResult
        if (!this.isQueued(r)) return 'Part of this split couldn\'t be processed right now.'
        return r.request
          ? 'Polly couldn\'t take it right now.'
          : 'This concession couldn\'t be processed right now.'
      }
    },
    async mounted() {
      await this.load()
    },
    methods: {
      emptyForm() {
        return {
          loan_id: '',
          loan_number: '',
          borrower_first: '',
          borrower_last: '',
          loan_amount: null,
          orgid: '',
          channel: '',
          product_type: '',
          tier: 'branch',
          requested_bps: null,
          requested_amount: null,
          // Split only. Each side's own share, in bps.
          split_branch_bps: null,
          split_corp_bps: null,
          reason_code: '',
          requester_notes: ''
        }
      },
      async load() {
        this.loading = true
        this.loadError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          // Both at once — the loan does not depend on the context.
          const [contextRes, loanRes] = await Promise.all([
            api.GetConcessionUserContext(token),
            this.loanId
              ? api.GetConcessionPipelineLoan(this.loanId, token)
              : Promise.resolve(null)
          ])

          const context = contextRes?.data?.data
          if (context) {
            this.context = { ...this.context, ...context }
          }

          if (this.loanId) {
            const body = loanRes?.data || {}
            const loan = body.data
            if (!loan) {
              this.loadError = body.message || 'That loan could not be found.'
              return
            }
            this.loan = loan
            this.applyLoanToForm(loan)
          } else {
            // Manual path: the product default is safe to offer because the user is
            // typing everything anyway.
            this.form.product_type = 'conv'
          }

        } catch (e) {
          this.loadError = this.serverMessage(e, 'Could not load this loan.')
        } finally {
          this.loading = false
        }
      },
      // Everything the loan already knows. product_type is deliberately left blank when
      // Encompass has something we don't price against — defaulting it to conventional
      // would silently pick the wrong ceiling.
      applyLoanToForm(loan) {
        this.form.loan_id = loan.loan_id || ''
        this.form.loan_number = loan.loan_number || ''
        this.form.borrower_first = loan.borrower_first || ''
        this.form.borrower_last = loan.borrower_last || ''
        this.form.loan_amount = loan.loan_amount ?? null
        this.form.orgid = loan.orgid || ''
        this.form.channel = loan.channel || ''
        this.form.product_type = loan.product_type || ''
      },
      resetForm() {
        const loan = this.loan
        this.form = this.emptyForm()
        if (loan) this.applyLoanToForm(loan)
        else this.form.product_type = 'conv'
        this.submitResult = null
        this.submitError = ''
        this.isPreview = false
      },
      // One payload builder so a preview cannot check something different from what a
      // submit then files.
      buildPayload() {
        return {
          loan_id: this.form.loan_id || null,
          loan_number: this.form.loan_number || null,
          borrower_first: this.form.borrower_first || null,
          borrower_last: this.form.borrower_last || null,
          loan_amount: this.form.loan_amount || null,
          orgid: this.form.orgid || null,
          channel: this.form.channel || null,
          tier: this.form.tier,
          product_type: this.form.product_type,
          requested_bps: this.isSplit ? null : (this.form.requested_bps || null),
          requested_amount: this.isSplit ? null : (this.form.requested_amount || null),
          split_branch_bps: this.isSplit ? (this.form.split_branch_bps || null) : null,
          split_corp_bps: this.isSplit ? (this.form.split_corp_bps || null) : null,
          reason_code: this.form.reason_code,
          requester_notes: this.form.requester_notes || null
        }
      },
      async preview() {
        if (!this.canSubmit || this.submitting) return

        this.submitting = true
        this.previewing = true
        this.submitError = ''
        this.submitResult = null

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.PreviewConcessionRequest(this.buildPayload(), tokenResponse.accessToken)
          this.submitResult = res?.data?.data || null
          this.isPreview = true
        } catch (e) {
          this.submitError = this.serverMessage(e, 'Could not preview the concession.')
        } finally {
          this.submitting = false
          this.previewing = false
        }
      },
      async submit() {
        if (!this.canSubmit || this.submitting || this.filed) return

        this.submitting = true
        this.submitError = ''
        this.submitResult = null
        this.isPreview = false

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.SubmitConcessionRequest(this.buildPayload(), tokenResponse.accessToken)
          this.submitResult = res?.data?.data || null
          // This tab exists to file one concession. Locking the buttons is what stops a
          // second one going in on a double-click or a stray refresh-and-resubmit.
          this.filed = true
        } catch (e) {
          this.submitError = this.serverMessage(e, 'Could not submit the concession. Nothing was recorded.')
        } finally {
          this.submitting = false
        }
      },

      // ── display helpers ──────────────────────────────────────────────────
      // Saved but not finished: being retried, or handed (or being handed) to the lock desk. All
      // three read the same to the submitter — it's in hand, don't file it again.
      isQueued(result) {
        return ['retrying', 'escalating', 'escalated'].includes(result?.queue_status)
      },
      serverMessage(error, fallback) {
        return error?.response?.data?.message || fallback
      },
      productLabel,
      // Reporting dates are plain calendar dates, NOT UTC stamps — appending a Z would
      // slide a midnight lock expiry back a day.
      formatLoanDate(value) {
        if (!value) return '—'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return '—'
        return date.toLocaleDateString('en-US')
      },
      formatRate(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toFixed(3) + '%'
      },
      formatNumber(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
      },
      formatMoney(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
      },
      formatPrice(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toFixed(3)
      },
      // "0 bps" means nothing is conceded. "—" means we could not work it out — the
      // server returns a null bps when there is no usable loan amount to divide by.
      // Those must not render the same, or the page would assert a zero it does not know.
      // bps of the loan amount, as dollars. Blank when either side is missing, so a
      // hint never reads "≈ $0" just because nothing has been typed yet.
      bpsToMoney(bps) {
        const value = Number(bps) || 0
        const loanAmount = Number(this.form.loan_amount) || 0
        if (value <= 0 || loanAmount <= 0) return ''
        return this.formatMoney((value / 10000) * loanAmount)
      },
      bpsLabel(bps, amount) {
        if (bps !== null && bps !== undefined && !Number.isNaN(Number(bps))) {
          return `${this.formatNumber(bps)} bps`
        }
        return Number(amount) ? '—' : '0 bps'
      },
      // The breakdown arrives in dollars; this panel speaks bps like the rest of the page.
      bpsOf(amount) {
        const value = Number(amount) || 0
        const base = Number(this.loan?.total_loan_amount) || Number(this.loan?.loan_amount) || 0
        if (base <= 0) return '—'
        return `${this.formatNumber((value / base) * 10000)} bps`
      }
    }
  }
</script>

<style scoped>
  .request-page {
    padding: 20px 24px 40px;
    background: #f2f4f8;
    min-height: 100vh;
    color: #1c2433;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }

  .page-header { margin-bottom: 18px; }
  .page-header-left { display: flex; gap: 12px; align-items: center; }
  .page-icon {
    width: 38px; height: 38px; border-radius: 9px;
    background: #17315c; color: #ffffff;
    display: flex; align-items: center; justify-content: center; font-size: 17px;
  }
  .page-title { font-size: 21px; font-weight: 600; margin: 0; }
  .page-subtitle { font-size: 12.5px; color: #7b8397; margin: 2px 0 0; }

  /* Two columns, because the left half is reference the requester reads WHILE
     filling in the right half. Collapses to one on a narrow screen, where side by
     side would squeeze both to nothing. */
  .request-split {
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: 16px;
    align-items: start;
  }
  @media (max-width: 1000px) {
    .request-split { grid-template-columns: minmax(0, 1fr); }
  }

  .panel {
    background: #ffffff;
    border: 1px solid #e4e9f2;
    border-radius: 8px;
    padding: 18px;
  }
  .panel-title {
    font-size: 14px; font-weight: 600; margin: 0 0 14px;
    display: flex; align-items: center; gap: 8px; color: #1c2433;
  }
  .panel-subtitle {
    font-size: 12px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.4px; color: #7b8397; margin: 18px 0 8px;
  }

  .fact-list { margin: 0; display: grid; gap: 0; }
  .fact {
    display: flex; justify-content: space-between; gap: 12px;
    padding: 7px 0; border-bottom: 1px solid #f0f3f8; font-size: 13px;
  }
  .fact:last-child { border-bottom: none; }
  .fact dt { color: #7b8397; }
  .fact dd { margin: 0; text-align: right; font-weight: 500; color: #1c2433; }

  .on-file-strip { display: flex; flex-wrap: wrap; gap: 8px; }
  .on-file-cell {
    flex: 1 1 96px; background: #f9fbff; border: 1px solid #e4e9f2;
    border-radius: 6px; padding: 8px 10px;
  }
  .on-file-cell.is-total { border-color: #c2d4f0; background: #eef4ff; }
  .on-file-value { font-size: 14px; font-weight: 600; color: #1c2433; }
  .on-file-label { font-size: 10.5px; color: #7b8397; margin-top: 2px; }

  .on-file-note {
    margin-top: 10px; padding: 8px 10px; font-size: 12px;
    color: #7a5a12; background: #fdf6e3; border: 1px solid #efdca8; border-radius: 6px;
  }
  .raised-under {
    margin-top: 14px; font-size: 12px; color: #5a6478; line-height: 1.5;
  }

  .requester-line {
    font-size: 12.5px; color: #3c4658; background: #f6f8fc;
    border: 1px solid #e4e9f2; border-radius: 6px; padding: 9px 12px; margin-bottom: 16px;
  }

  .form-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 14px;
  }
  .form-field { display: flex; flex-direction: column; gap: 5px; }
  .form-field.full { margin-top: 14px; }
  .form-label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.4px; color: #7b8397;
  }
  .req { color: #c0392b; }
  .form-input {
    height: 38px; border: 1px solid #d9dfeb; border-radius: 5px;
    padding: 0 10px; font-size: 13px; color: #1c2433; background: #ffffff;
    font-family: inherit;
  }
  .form-textarea { height: auto; padding: 8px 10px; resize: vertical; }
  .form-hint { font-size: 11px; color: #7b8397; }
  .form-hint.is-warn { color: #a4472f; }

  .amount-row {
    display: flex; align-items: flex-end; gap: 12px; margin-top: 14px; flex-wrap: wrap;
  }
  .amount-row .form-field { flex: 1 1 150px; }
  .amount-or { font-size: 12px; color: #7b8397; padding-bottom: 11px; }
  .amount-derived { font-size: 12.5px; color: #3c4658; padding-bottom: 11px; }

  .split-total {
    margin-top: 10px; padding: 8px 12px; font-size: 12.5px; color: #1c2433;
    background: #f6f8fc; border: 1px solid #e4e9f2; border-radius: 6px;
  }
  .split-total .is-muted { color: #7b8397; }

  .split-parts { margin-top: 8px; display: grid; gap: 6px; }
  .split-part { display: flex; gap: 8px; align-items: baseline; font-size: 12.5px; }
  .split-part-tier {
    flex: 0 0 74px; font-size: 10.5px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.4px; color: #7b8397;
  }
  .split-part-body { color: #3c4658; }
  .split-part-body .is-ok { color: #1b7a47; }
  .split-part-body .is-warn { color: #7a5b12; }

  .running-total {
    margin-top: 12px; padding: 9px 12px; font-size: 12.5px; color: #123a71;
    background: #eef4ff; border: 1px solid #c2d4f0; border-radius: 6px;
  }

  /* Deliberately not styled as a warning: a giveback is the good direction. */
  .giveback-hint {
    margin-top: 10px; padding: 9px 12px; font-size: 12.5px; color: #1c5137;
    background: #eef7f1; border: 1px solid #c3e0ce; border-radius: 6px;
  }

  .giveback-hint i { margin-right: 6px; }

  .inline-error {
    margin-top: 14px; padding: 9px 12px; font-size: 12.5px;
    color: #8c2f1d; background: #fdf0ec; border: 1px solid #f3cec2; border-radius: 6px;
  }

  .outcome-card {
    margin-top: 16px; padding: 12px 14px; border-radius: 7px;
    border: 1px solid #e4e9f2; background: #f8fafd; font-size: 12.5px; line-height: 1.55;
  }
  .outcome-card.is-approved { border-color: #b8e5cc; background: #f2fbf6; }
  .outcome-card.is-pending { border-color: #efdca8; background: #fdf9ee; }
  .outcome-card.is-preview { border-color: #c2d4f0; background: #f4f8ff; }
  .outcome-head { display: flex; align-items: center; gap: 8px; font-size: 13.5px; margin-bottom: 6px; }
  .outcome-pricing { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 6px; color: #3c4658; }
  .outcome-text { color: #3c4658; margin-top: 4px; }
  .outcome-text.is-warn { color: #7a5b12; }
  .outcome-text.is-ok { color: #1b7a47; }
  .outcome-done {
    margin-top: 10px; padding-top: 10px; border-top: 1px solid #dfe6f1;
    font-weight: 600; color: #1c2433;
  }

  .form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }
  .cancel-btn, .preview-btn, .save-btn {
    height: 36px; padding: 0 16px; border-radius: 5px; font-size: 13px;
    font-weight: 600; cursor: pointer; border: 1px solid #d9dfeb; background: #ffffff; color: #3c4658;
  }
  .preview-btn { border-color: #c2d4f0; color: #2f6fd0; }
  .save-btn { background: #17315c; border-color: #17315c; color: #ffffff; }
  .cancel-btn:disabled, .preview-btn:disabled, .save-btn:disabled {
    opacity: 0.5; cursor: not-allowed;
  }

  .state-card, .blocked-card {
    background: #ffffff; border: 1px solid #e4e9f2; border-radius: 8px;
    padding: 18px; font-size: 13px; color: #3c4658;
  }
  .blocked-card { display: flex; gap: 14px; align-items: flex-start; }
  .blocked-icon { font-size: 22px; color: #a4472f; }
  .blocked-title { font-size: 15px; font-weight: 600; margin-bottom: 3px; }
  .blocked-text { color: #7b8397; line-height: 1.6; }

  .roster-note-card {
    display: flex; gap: 12px; align-items: flex-start; background: #eef4ff;
    border: 1px solid #c2d4f0; border-radius: 8px; padding: 12px 14px;
    margin-bottom: 14px; font-size: 12.5px; line-height: 1.5; color: #123a71;
  }
  .roster-note-icon { font-size: 16px; }
  .roster-note-text { flex: 1; }

  .sub-note { font-size: 11px; color: #7b8397; margin-left: 6px; }
  .sub-note.is-warn { color: #7a5b12; font-weight: 600; }
  .diag-flag {
    display: inline-block; font-size: 10px; font-weight: 700; padding: 1px 6px;
    border-radius: 8px; margin-left: 6px;
  }
  .diag-flag.is-warn { background: #fdf3d7; color: #7a5b12; }
</style>
